import React, { useRef, useEffect, useState } from "react";

import ArrowBack from "@material-ui/icons/ArrowBack";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";



import {
  Wrapper,
  InputWrapper,
  ButtonWrapper,
  PasswordToggleButton,
  FormHeader,
  FormTitle,
  FormSubtitle,
  StepBar,
  StepSegment,
  SectionNav,
  BackIconButton,
  Divider,
  GoogleButton,
} from "./styles";
import Input from "../../../common/CustomInput";
import { Button } from "../../../../design-system";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const STEPS = [
  { title: "Create your account", subtitle: "Let's start with your name" },
  { title: "Contact details", subtitle: "How can we reach you?" },
  { title: "Set your password", subtitle: "Keep your account secure" },
];

type Props = {
  setSnackbarMessage: Function;
  setSnackbarError: Function;
  createUser: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    company: string,
    phoneNumber: string
  ) => void;
  onGoogleSignIn: () => Promise<void>;
};

const RegisterUserForm = ({ setSnackbarError, createUser, onGoogleSignIn }: Props) => {
  const [step, setStep] = useState(0);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [repeatedPassword, setRepeatedPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const firstInputRef = useRef<any>(null);

  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [step]);

  const handleSubmit = (): void => {
    if (password !== repeatedPassword) {
      setSnackbarError("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      setSnackbarError("Password must be at least 6 characters");
      return;
    }
    setIsLoading(true);
    const [firstName, ...rest] = fullName.trim().split(" ");
    createUser(email, password, firstName, rest.join(" "), company, phoneNumber);
  };

  const handleGoogleSignIn = (): void => {
    setIsGoogleLoading(true);
    onGoogleSignIn().catch((error) => {
      setIsGoogleLoading(false);
      setSnackbarError(error.message);
    });
  };

  const passwordsMatch =
    !!password && !!repeatedPassword && password === repeatedPassword;
  const canSubmit = !!fullName && !!email && passwordsMatch && !isLoading;

  return (
    <Wrapper>
      <StepBar>
        {STEPS.map((_, i) => (
          <StepSegment key={i} active={i <= step} />
        ))}
      </StepBar>
      <FormHeader>
        {step > 0 && (
          <SectionNav>
            <BackIconButton type="button" onClick={() => setStep(step - 1)}>
              <ArrowBack fontSize="small" />
            </BackIconButton>
          </SectionNav>
        )}
        <FormTitle>{STEPS[step].title}</FormTitle>
        <FormSubtitle>{STEPS[step].subtitle}</FormSubtitle>
      </FormHeader>

      {step === 0 && (
        <>
          <InputWrapper>
            <Input
              inputRef={(input) => {
                firstInputRef.current = input;
              }}
              onChange={(event: any) => setFullName(event.target.value)}
              onBlur={() => {}}
              placeholder="John Doe"
              value={fullName}
              label="Full Name"
              type="text"
              id="fullName"
              error=""
            />
          </InputWrapper>
          <ButtonWrapper>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              disabled={!fullName.trim()}
              onClick={() => setStep(1)}
            >
              Continue
            </Button>
          </ButtonWrapper>
          <Divider>or</Divider>
          <GoogleButton onClick={handleGoogleSignIn} disabled={isGoogleLoading} type="button">
            <GoogleIcon />
            {isGoogleLoading ? "Signing in…" : "Continue with Google"}
          </GoogleButton>
        </>
      )}

      {step === 1 && (
        <>
          <InputWrapper>
            <Input
              inputRef={(input) => {
                firstInputRef.current = input;
              }}
              onChange={(event: any) => setEmail(event.target.value)}
              onBlur={() => {}}
              value={email}
              label="Work Email"
              placeholder="you@company.com"
              id="email"
              type="email"
              error=""
            />
          </InputWrapper>
          <InputWrapper>
            <Input
              onChange={(event: any) => setCompany(event.target.value)}
              onBlur={() => {}}
              value={company}
              type="text"
              placeholder="Acme Corp"
              label="Company"
              id="company"
              error=""
            />
          </InputWrapper>
          <InputWrapper>
            <Input
              onChange={(event: any) => setPhoneNumber(event.target.value)}
              onBlur={() => {}}
              value={phoneNumber}
              type="tel"
              placeholder="+1 (555) 000-0000"
              label="Phone Number"
              id="phoneNumber"
              error=""
            />
          </InputWrapper>
          <ButtonWrapper>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              disabled={!email.trim()}
              onClick={() => setStep(2)}
            >
              Continue
            </Button>
          </ButtonWrapper>
        </>
      )}

      {step === 2 && (
        <>
          <InputWrapper>
            <Input
              inputRef={(input) => {
                firstInputRef.current = input;
              }}
              onChange={(event: any) => setPassword(event.target.value)}
              onBlur={() => {}}
              value={password}
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              label="Password"
              id="password"
              error=""
            />
            <PasswordToggleButton
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? (
                <VisibilityOff fontSize="small" />
              ) : (
                <Visibility fontSize="small" />
              )}
            </PasswordToggleButton>
          </InputWrapper>
          <InputWrapper>
            <Input
              onChange={(event: any) => setRepeatedPassword(event.target.value)}
              onBlur={() => {}}
              value={repeatedPassword}
              type={showConfirmPassword ? "text" : "password"}
              placeholder="••••••••"
              label="Confirm Password"
              id="repeatedPassword"
              error=""
            />
            <PasswordToggleButton
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              tabIndex={-1}
            >
              {showConfirmPassword ? (
                <VisibilityOff fontSize="small" />
              ) : (
                <Visibility fontSize="small" />
              )}
            </PasswordToggleButton>
          </InputWrapper>
          <ButtonWrapper>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              disabled={!canSubmit}
              isLoading={isLoading}
              onClick={handleSubmit}
            >
              Create Account
            </Button>
          </ButtonWrapper>
        </>
      )}
    </Wrapper>
  );
};

export default RegisterUserForm;
